/**
 * Тонкая обёртка над IndexedDB.
 *
 * Библиотека не используется намеренно: нужен один object store и три
 * операции, а лишняя зависимость в критическом пути загрузки заметна на
 * медленном соединении.
 */

const DEFAULT_DB_NAME = 'type2x';
const DEFAULT_VERSION = 1;

/** Имя хранилища личных пространств. */
export const WORKSPACE_STORE = 'workspaces';

/** Имя хранилища очереди синхронизации. */
export const SYNC_QUEUE_STORE = 'sync-queue';

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Ошибка IndexedDB'));
  });
}

/** Открывает базу и создаёт хранилища при первом запуске. */
export function openDatabase(
  name: string = DEFAULT_DB_NAME,
  version: number = DEFAULT_VERSION,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = globalThis.indexedDB.open(name, version);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(WORKSPACE_STORE)) {
        db.createObjectStore(WORKSPACE_STORE, { keyPath: 'installationId' });
      }
      if (!db.objectStoreNames.contains(SYNC_QUEUE_STORE)) {
        db.createObjectStore(SYNC_QUEUE_STORE, { keyPath: 'syncId' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Не удалось открыть IndexedDB'));
  });
}

/** Читает запись по ключу. */
export async function readRecord<T>(
  db: IDBDatabase,
  store: string,
  key: IDBValidKey,
): Promise<T | null> {
  const transaction = db.transaction(store, 'readonly');
  const result = await promisify<T | undefined>(transaction.objectStore(store).get(key));
  return result ?? null;
}

/** Записывает или обновляет запись. */
export async function writeRecord(db: IDBDatabase, store: string, value: unknown): Promise<void> {
  const transaction = db.transaction(store, 'readwrite');
  await promisify(transaction.objectStore(store).put(value));
}

/** Удаляет запись по ключу. */
export async function deleteRecord(
  db: IDBDatabase,
  store: string,
  key: IDBValidKey,
): Promise<void> {
  const transaction = db.transaction(store, 'readwrite');
  await promisify(transaction.objectStore(store).delete(key));
}

/** Удаляет все записи хранилища. */
export async function clearStore(db: IDBDatabase, store: string): Promise<void> {
  const transaction = db.transaction(store, 'readwrite');
  await promisify(transaction.objectStore(store).clear());
}

/** Все записи хранилища. */
export async function listRecords<T>(db: IDBDatabase, store: string): Promise<T[]> {
  const transaction = db.transaction(store, 'readonly');
  return promisify<T[]>(transaction.objectStore(store).getAll());
}
